import React, {
  useState,
  useEffect,
  useDeferredValue,
  useCallback,
} from "react";
import { Box, IconButton, InputBase, Button, CircularProgress } from "@mui/material";
import type { GridPaginationModel } from "@mui/x-data-grid";
import { FilterListOutlined, SearchOutlined } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/Index";
import apiService from "../../../../service/Api";
import CalendarViewWeekIcon from "@mui/icons-material/CalendarViewWeek";
import ViewHeadlineIcon from "@mui/icons-material/ViewHeadline";
import { showErrorToast } from "../../../../components/common/ShowToast";
import { MemoryBookStats } from "./components/MemoryBookStats";
import { TableView } from "./components/TableView";
import MemoryBookKanban, {
  KanbanStreamInput,
} from "./components/MemoryBookKanban";
import { User } from "../../../users-admin/types/User";

interface Assignment {
  id: number;
  assigned_to: User;
  assigned_by: User;
  physical_comments: number | null;
  done_comments: number | null;
  assigned_at: string;
  updated_at: string;
  remaining_comments?: number;
  due_date?: string;
}

interface MemoryBookStreamInfo {
  memory_book_id: number | null;
  memory_book_status: "pending" | "in-progress" | "done" | "printing" | null;
  is_delivered: boolean;
  is_memory_book_available: boolean;
  approved_comments: number;
  total_comments: number;
  canva_memory_book_link: string | null;
  assignees: User[];
  assignments: Assignment[];
  created_at: string;
  last_updated_by: User;
  name_of_deceased: string;
  stream_date: string;
  stream_id: number;
  updated_at: string;
  physical_comments?: number;
  done_comments?: number;
  due_date?: string;
  assigned_at?: string;
}

const transformStreamInfo = (
  results: MemoryBookStreamInfo[],
): MemoryBookStreamInfo[] =>
  results.map((book) => {
    const uniqueAssignees = Array.from(
      new Map(
        book.assignments.map((a) => [a.assigned_to.id, a.assigned_to]),
      ).values(),
    );

    const assigneesToUse =
      book.assignees.length > 0 ? book.assignees : uniqueAssignees;
    const firstAssignment = book.assignments[0];

    return {
      ...book,
      assignees: assigneesToUse,
      physical_comments: book.physical_comments || 0,
      done_comments: book.done_comments || 0,
      assigned_at: firstAssignment?.assigned_at || book.created_at,
      due_date: firstAssignment?.due_date || book.due_date,
    };
  });

const MemoryTableListing: React.FC = () => {
  const [appliedSearchTerm, setAppliedSearchTerm] = useState<string>("");
  const defferedSearchTerm = useDeferredValue(appliedSearchTerm);
  const [tableViewStreamInfo, setTableViewStreamsInfo] = useState<
    MemoryBookStreamInfo[]
  >([]);
  const [kanbanStreamInfo, setKanbanStreamInfo] = useState<
    MemoryBookStreamInfo[]
  >([]);
  const [viewMode, setViewMode] = useState<"table" | "grid">("table");
  const [isLoading, setIsLoading] = useState(true);
  const [isKanbanLoading, setIsKanbanLoading] = useState(true);
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const [rowCount, setRowCount] = useState(0);

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });

  const handlePaginationModelChange = (newModel: GridPaginationModel) => {
    setPaginationModel(newModel);
  };

  const fetchStreams = useCallback(async () => {
    setIsLoading(true);

    try {
      const params = {
        page: paginationModel.page + 1,
        page_size: paginationModel.pageSize,
        search: defferedSearchTerm,
        ordering: "-id",
        booking_type: "FUNERAL",
      };

      const response = await apiService.get<{
        count: number;
        next: string | null;
        previous: string | null;
        results: MemoryBookStreamInfo[];
      }>("/api/streams/stream/memory-book/", params);

      setRowCount(response.data.count);
      setTableViewStreamsInfo(transformStreamInfo(response.data.results));
    } catch (err: unknown) {
      console.error("Failed to load streams:", err);
      showErrorToast("Failed to load streams. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [defferedSearchTerm, paginationModel.page, paginationModel.pageSize]);

  // The kanban shows the whole pipeline at once (grouped into status
  // columns), not one page at a time, so it needs its own unpaginated-ish
  // fetch rather than sharing the table's 10-per-page result set.
  const fetchKanbanStreams = useCallback(async () => {
    setIsKanbanLoading(true);

    try {
      const params = {
        page_size: 1000,
        search: defferedSearchTerm,
        ordering: "-id",
        booking_type: "FUNERAL",
      };

      const response = await apiService.get<{
        count: number;
        results: MemoryBookStreamInfo[];
      }>("/api/streams/stream/memory-book/", params);

      setKanbanStreamInfo(transformStreamInfo(response.data.results));
    } catch (err: unknown) {
      console.error("Failed to load kanban streams:", err);
      showErrorToast("Failed to load streams. Please try again.");
    } finally {
      setIsKanbanLoading(false);
    }
  }, [defferedSearchTerm]);

  useEffect(() => {
    if (viewMode === "table") fetchStreams();
  }, [fetchStreams, viewMode]);

  useEffect(() => {
    if (viewMode === "grid") fetchKanbanStreams();
  }, [fetchKanbanStreams, viewMode]);

  const updateSearchTerm = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setAppliedSearchTerm(e.target.value);
  };

  const handleColumnChange = async ({
    memoryBookId,
    toColumn,
  }: {
    streamId: number;
    memoryBookId: number;
    fromColumn: string;
    toColumn: string;
  }) => {
    const payload =
      toColumn === "delivered" ? { is_delivered: true } : { status: toColumn };
    await apiService.patch(
      `/api/streams/memory-book/${memoryBookId}/`,
      payload,
    );
    fetchKanbanStreams();
  };

  const kanbanStreams: KanbanStreamInput[] = kanbanStreamInfo.map((s) => ({
    stream_id: s.stream_id,
    name_of_deceased: s.name_of_deceased,
    memory_book_id: s.memory_book_id,
    memory_book_status: s.memory_book_status,
    is_memory_book_available: s.is_memory_book_available,
    is_delivered: s.is_delivered,
    total_comments: s.total_comments,
    last_updated_by: s.last_updated_by,
    assignees: s.assignees,
  }));

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <MemoryBookStats />
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          gap: 2,
          mb: 2,
        }}
      >
        <Box
          sx={{
            backgroundColor: reduxTheme.primary_colors[900],
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            pl: 1,
            width: { xs: "100%", sm: "50%", md: "25%" },
          }}
        >
          <SearchOutlined sx={{ mr: 1 }} />
          <InputBase
            placeholder="Search by title, service, or package..."
            onChange={(e) => updateSearchTerm(e)}
            sx={{ ml: 1, flex: 1 }}
          />
          <IconButton sx={{ width: 44, height: 44 }}>
            <FilterListOutlined />
          </IconButton>
        </Box>

        <Box
          sx={{ display: "flex", gap: 1, width: { xs: "100%", sm: "auto" } }}
        >
          <Button
            variant="contained"
            sx={{
              flex: { xs: 1, sm: "unset" },
              minWidth: 44,
              minHeight: 44,
              bgcolor:
                viewMode === "table"
                  ? "primary"
                  : reduxTheme.primary_colors[800],
            }}
            onClick={() => setViewMode("table")}
          >
            <ViewHeadlineIcon
              sx={{
                color:
                  viewMode === "table"
                    ? reduxTheme.primary_colors[900]
                    : reduxTheme.primary_colors[700],
              }}
            />
          </Button>
          <Button
            variant="contained"
            onClick={() => setViewMode("grid")}
            sx={{
              flex: { xs: 1, sm: "unset" },
              minWidth: 44,
              minHeight: 44,
              bgcolor:
                viewMode === "grid"
                  ? "primary"
                  : reduxTheme.primary_colors[800],
            }}
          >
            <CalendarViewWeekIcon
              sx={{
                color:
                  viewMode === "grid"
                    ? reduxTheme.primary_colors[900]
                    : reduxTheme.primary_colors[700],
              }}
            />
          </Button>
        </Box>
      </Box>

      {viewMode === "grid" && (
        isKanbanLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <MemoryBookKanban
            streams={kanbanStreams}
            onColumnChange={handleColumnChange}
          />
        )
      )}

      {viewMode === "table" && (
        <TableView
          tableViewStreams={tableViewStreamInfo}
          isLoading={isLoading}
          paginationModel={paginationModel}
          rowCount={rowCount}
          handlePaginationModelChange={handlePaginationModelChange}
          onAssignSuccess={fetchStreams}
        />
      )}
    </Box>
  );
};

export default MemoryTableListing;
